/* eslint-disable react-hooks/exhaustive-deps */
import { Grid, makeStyles, Typography } from '@material-ui/core';
import PageTitle from 'components/PageTitle';
import RefreshButton from 'components/RefreshButton';
import AddIcon from '@material-ui/icons/Add';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import i18n from 'i18n';
import React from 'react';
import Permission from 'components/ValidPermission';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { cyrb53Hash } from 'utils/helpers';
import {
  deleteDigitalCertificatesRequest,
  getDigitalCertificatesRequest,
} from 'modules/clients/actions';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import {
  DigitalCertificatesProps,
  ListDigitalCertificatesDataProps,
} from 'modules/clients/interfaces';
import { PermissionsData } from 'modules/authenticate/interfaces';
import { ModalFields } from 'modules/modal/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { useParams } from 'react-router';
import Button from 'components/Button';
import styles from './styles';
import FormCertificate from './FormCertificate';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 25,
  search: '',
};

interface Props {
  isLoadingCertificates: boolean;
  digitalCertificates: ListDigitalCertificatesDataProps;
  title?: string;
  subtitle?: string;
  permissions?: PermissionsData;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  getDigitalCertificatesRequest: (props) => void;
  deleteDigitalCertificatesRequest: (props) => void;
}

const ListAllCertificates = ({
  isLoadingCertificates,
  digitalCertificates,
  title,
  subtitle,
  permissions,
  modalClose,
  modalOpen,
  getDigitalCertificatesRequest,
  deleteDigitalCertificatesRequest,
}: Props) => {
  const classes = useStyles();

  const {
    itemOrder,
    query,
    searchValue,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const [refresh, setRefresh] = React.useState(false);
  const { id } = useParams<{ id: string }>();

  const headers = [
    {
      key: 'name',
      i18n: 'NAME_KEY',
      order: true,
      style: { width: '45%' },
    },
    {
      key: 'path',
      i18n: 'DIGITAL_CERTIFICATE_PATH_KEY',
      order: true,
      style: { width: '45%' },
    },
    {
      key: '',
      i18n: 'ACTIONS_KEY',
      style: { width: '10%' },
      centerTitle: true,
      textAlignCenter: true,
      hidden: !!permissions && !permissions.can_manage_client_certificates,
    },
  ].filter(item => !item.hidden);

  const getDigitalCertificates = (refresh = false) => {
    getDigitalCertificatesRequest({
      clientUuid: id,
      ...query,
    });

    setRefresh(refresh);
  };

  React.useEffect(() => {
    getDigitalCertificates();
  }, [query]);

  const openCreateModal = () => {
    modalOpen({
      title: i18n.t('CREATE_DIGITAL_CERTIFICATE_KEY'),
      content: (
        <FormCertificate
          mode="create"
          clientUuid={id}
          query={query}
          onCancel={modalClose}
          onCloseModal={modalClose}
        />
      ),
    });
  };

  const openEditModal = (item: DigitalCertificatesProps) => {
    modalOpen({
      title: i18n.t('EDIT_DIGITAL_CERTIFICATE_KEY'),
      content: (
        <FormCertificate
          mode="edit"
          clientUuid={id}
          certificateUuid={item?.uuid}
          initialName={item?.name}
          initialPath={item?.path}
          query={query}
          onCancel={modalClose}
          onCloseModal={modalClose}
        />
      ),
    });
  };

  const openDeleteConfirmModal = (item: DigitalCertificatesProps) => {
    modalOpen({
      title: i18n.t('DELETE_DIGITAL_CERTIFICATE_KEY'),
      content: (
        <Typography>
          {i18n.t('CONFIRM_DELETE_DIGITAL_CERTIFICATE_KEY')} <b>{item?.name}</b>
          ?
        </Typography>
      ),
      footer: (
        <>
          <Button
            id="confirm-delete-certificate"
            deleteButton
            onClick={() => {
              deleteDigitalCertificatesRequest({
                clientUuid: id,
                certificateUuid: item?.uuid,
                query,
              });
              modalClose();
            }}
          >
            {i18n.t('DELETE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CLOSE_KEY')}
          </Button>
        </>
      ),
    });
  };

  return (
    <Grid container>
      <Grid
        container
        direction="row"
        alignItems="center"
        justify="space-between"
        className={classes.titleMargin}
      >
        <PageTitle
          title={title || i18n.t('DIGITAL_CERTIFICATES_KEY')}
          subtitle={subtitle || i18n.t('LIST_DIGITAL_CERTIFICATES_KEY')}
        />

        <Grid item>
          <RefreshButton
            id="btn-refresh"
            onClick={() => getDigitalCertificates(true)}
            animate={isLoadingCertificates}
            className={classes.refreshButton}
            disabled={isLoadingCertificates}
          />
        </Grid>
      </Grid>

      <Grid item xs={12} className={classes.searchContainer}>
        <Grid container spacing={2} alignItems="center" justify="space-between">
          <Grid item xs={12} md className={classes.searchSize}>
            <SearchBar
              id="search-certificates"
              isLoading={isLoadingCertificates}
              searchValue={searchValue}
              handleSearch={handleSearch}
              setSearchValue={setSearchValue}
            />
          </Grid>

          <Permission value="can_manage_client_certificates">
            <Grid item>
              <Button
                id="btn-create-certificate"
                variant="contained"
                color="primary"
                onClick={openCreateModal}
                disabled={isLoadingCertificates}
                className={classes.addButton}
                startIcon={<AddIcon />}
              >
                {i18n.t('CREATE_DIGITAL_CERTIFICATE_KEY')}
              </Button>
            </Grid>
          </Permission>
        </Grid>
      </Grid>
      <Grid container className={classes.searchContainer}>
        <TableItems
          header={
            <>
              {headers.map(item => (
                <TableCell
                  textAlignCenter={item?.textAlignCenter}
                  centerTitle={item?.centerTitle}
                  key={item?.i18n}
                  order={item?.order}
                  markOderItem={itemOrder === item?.key}
                  style={item?.style}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                >
                  {i18n.t(item?.i18n)}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: DigitalCertificatesProps, index) => (
            <TableRow key={cyrb53Hash(String(index))}>
              <TableCell maxLength={30} ellipsis className={classes.textLong}>
                <Typography className={classes.nameField}>
                  {item?.name}
                </Typography>
              </TableCell>
              <TableCell maxLength={30} ellipsis className={classes.textLong}>
                <Typography className={classes.nameField}>
                  {item?.path}
                </Typography>
              </TableCell>
              <Permission value="can_manage_client_certificates">
                <TableCell
                  textAlignCenter
                  centerTitle
                  className={classes.actionsCell}
                >
                  <MenuActions id={`btn-options-certificates-${index}`}>
                    <MenuActionItem
                      id={`btn-edit-${index}`}
                      onClick={() => openEditModal(item)}
                    >
                      {i18n.t('EDIT_KEY')}
                    </MenuActionItem>

                    <MenuActionItem
                      id={`btn-delete-${index}`}
                      onClick={() => openDeleteConfirmModal(item)}
                    >
                      {i18n.t('DELETE_KEY')}
                    </MenuActionItem>
                  </MenuActions>
                </TableCell>
              </Permission>
            </TableRow>
          )}
          data={digitalCertificates}
          isLoading={!refresh && isLoadingCertificates}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={i18n.t('LOGS_PAGE.NO_LOGS_KEY')}
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  clients: { isLoadingCertificates, digitalCertificates },
  authenticate: { data },
}: ApplicationState) => ({
  isLoadingCertificates,
  digitalCertificates,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  getDigitalCertificatesRequest,
  deleteDigitalCertificatesRequest,
  modalClose,
  modalOpen,
})(ListAllCertificates);
